import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { MapPin, Package, CreditCard, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { API_URL } from '../config';

const PrivateTripComplete = () => {
  const { tripId } = useParams();
  const location = useLocation();
  const [tripDetails, setTripDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentTiming, setPaymentTiming] = useState('after');
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchTripDetails = async () => {
      if (!tripId) {
        console.error("No trip ID provided");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/trip/${tripId}`);
        if (!response.ok) {
          throw new Error("Failed to fetch trip details");
        }
        const data = await response.json();
        const trip = data[0];
        setTripDetails(trip);

        // Determine payment timing
        if (trip?.paying_when === 'Paying Before Trip') {
          setPaymentTiming('before');
          // Check if payment was already made
          if (trip?.payment_status === 'completed') {
            setPaymentCompleted(true);
          }
        } else {
          setPaymentTiming('after');
        }
      } catch (error) {
        console.error("Error fetching trip details:", error);
        alert("Failed to load trip details");
      } finally {
        setLoading(false);
      }
    };

    fetchTripDetails();
  }, [tripId]);

  const handleArrivedConfirmation = () => {
    if (paymentTiming === 'after' && !paymentCompleted) {
      // For post-trip payment, show payment modal when confirming arrival
      if (window.confirm('You have arrived at your destination. Please complete the payment to finish the trip.')) {
        setPaymentModalVisible(true);
      }
    } else if (paymentTiming === 'before' && !paymentCompleted) {
      // For pre-trip payment that wasn't completed
      alert('Please complete the pre-trip payment before you can confirm arrival.');
      setPaymentModalVisible(true);
    } else {
      // Payment already completed, just confirm arrival
      confirmArrival();
    }
  };

  const confirmArrival = async () => {
    setSubmitting(true);
    
    try {
      const updateData = {
        status: "Completed",
        completed_at: new Date().toISOString(),
      };

      const response = await fetch(`${API_URL}/trip/${tripDetails.trip_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        alert('Trip Completed! Thank you for using XGO. Your trip has been completed successfully.');
        
        // Navigate back to home
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'NAVIGATE_HOME'
          }));
        } else {
          window.close();
        }
      } else {
        throw new Error("Failed to update trip status");
      }
    } catch (error) {
      console.error("Error confirming arrival:", error);
      alert("Failed to complete trip. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentComplete = (paymentResult) => {
    console.log('Payment completed:', paymentResult);
    
    if (paymentResult.status === 'completed') {
      setPaymentCompleted(true);
      setPaymentModalVisible(false);
      
      alert(`Payment Successful! Payment of $${paymentResult.amount.toFixed(2)} completed successfully.`);
      
      // For post-trip payment, automatically confirm arrival
      if (paymentTiming === 'after') {
        confirmArrival();
      }
    } else {
      console.error('Payment failed or incomplete:', paymentResult);
      alert('There was an issue with your payment. Please try again.');
    }
  };

  const getPaymentStatusColor = () => {
    if (paymentCompleted || tripDetails?.payment_status === 'completed') return '#4CAF50';
    if (tripDetails?.payment_status === 'pending') return '#FF9800';
    return '#757575';
  };

  const getPaymentStatusText = () => {
    if (paymentCompleted || tripDetails?.payment_status === 'completed') return 'Paid';
    if (tripDetails?.payment_status === 'pending') return 'Pending';
    return 'Not Paid';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZW', {
      style: 'currency',
      currency: 'ZWL',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
        backgroundColor: '#F5F5F5'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #FFC000',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '10px'
        }}></div>
        <div style={{ fontSize: '16px', color: '#666' }}>Loading trip details...</div>
      </div>
    );
  }

  if (!tripDetails) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
        backgroundColor: '#F5F5F5'
      }}>
        <AlertCircle size={48} style={{ color: '#666', marginBottom: '10px' }} />
        <div style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
          Trip details not found
        </div>
        <button
          onClick={() => window.history.back()}
          style={{
            backgroundColor: '#FFC000',
            color: '#000',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '5px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  const {
    deliveray_details,
    notes,
    destination,
    status,
    delivery_cost_proposed,
    paying_when,
    payment_status,
    origin_location
  } = tripDetails;

  return (
    <div style={{
      maxWidth: '100%',
      minHeight: '100vh',
      backgroundColor: '#F5F5F5',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '60px 20px 20px',
        backgroundColor: '#FFF',
        borderBottom: '1px solid #E0E0E0'
      }}>
        <button
          onClick={() => window.history.back()}
          style={{
            padding: '8px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '24px'
          }}
        >
          ←
        </button>
        <div style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#000'
        }}>
          Trip Completion
        </div>
        <div style={{ width: '40px' }}></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ padding: '20px' }}
      >
        {/* Trip Status Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.3 }}
          style={{
            backgroundColor: '#FFF',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '10px'
          }}>
            <CheckCircle style={{ color: '#4CAF50', marginRight: '10px' }} size={32} />
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#333',
              margin: 0
            }}>
              Ready to Complete
            </h2>
          </div>
          <p style={{
            fontSize: '14px',
            color: '#666',
            margin: 0
          }}>
            Please confirm you have arrived at your destination
          </p>
        </motion.div>

        {/* Trip Details */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
          style={{
            backgroundColor: '#FFF',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#333',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Package size={18} style={{ marginRight: '8px', color: '#FFC000' }} />
            Trip Details
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <span style={{ fontSize: '14px', color: '#666', fontWeight: '500', flex: 1 }}>
                From:
              </span>
              <span style={{ 
                fontSize: '14px', 
                color: '#333',
                flex: 2,
                textAlign: 'right'
              }}>
                {origin_location || 'Pickup location'}
              </span>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <span style={{ fontSize: '14px', color: '#666', fontWeight: '500', flex: 1 }}>
                To:
              </span>
              <span style={{ 
                fontSize: '14px', 
                color: '#333',
                flex: 2,
                textAlign: 'right'
              }}>
                {destination || 'Destination'}
              </span>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <span style={{ fontSize: '14px', color: '#666', fontWeight: '500', flex: 1 }}>
                Description:
              </span>
              <span style={{ 
                fontSize: '14px', 
                color: '#333',
                flex: 2,
                textAlign: 'right'
              }}>
                {deliveray_details || 'No description'}
              </span>
            </div>
            
            {notes && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
              }}>
                <span style={{ fontSize: '14px', color: '#666', fontWeight: '500', flex: 1 }}>
                  Notes:
                </span>
                <span style={{ 
                  fontSize: '14px', 
                  color: '#333',
                  flex: 2,
                  textAlign: 'right'
                }}>
                  {notes}
                </span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Payment Information */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.3 }}
          style={{
            backgroundColor: '#FFF',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#333',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <CreditCard size={18} style={{ marginRight: '8px', color: '#FFC000' }} />
            Payment Information
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>
                Total Fare:
              </span>
              <span style={{ 
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#FFC000'
              }}>
                {formatCurrency(delivery_cost_proposed)}
              </span>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>
                Payment Type:
              </span>
              <span style={{ fontSize: '14px', color: '#333' }}>
                {paying_when || 'Paying After Trip'}
              </span>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>
                Status:
              </span>
              <div style={{
                padding: '4px 12px',
                borderRadius: '12px',
                backgroundColor: getPaymentStatusColor()
              }}>
                <span style={{ 
                  fontSize: '12px',
                  color: '#FFF',
                  fontWeight: '600'
                }}>
                  {getPaymentStatusText()}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Action Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.3 }}
        >
          <button
            onClick={handleArrivedConfirmation}
            disabled={submitting}
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign: 'center',
              cursor: submitting ? 'not-allowed' : 'pointer',
              backgroundColor: paymentCompleted ? '#4CAF50' : '#FFC000',
              color: '#FFF',
              transition: 'opacity 0.2s',
              opacity: submitting ? 0.6 : 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {paymentCompleted ? <CheckCircle size={20} /> : <CreditCard size={20} />}
            {submitting ? 'Processing...' : (paymentCompleted ? 'Confirm Arrival' : 'Confirm & Pay')}
          </button>
        </motion.div>

        {/* Instructions */}
        {!paymentCompleted && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.3 }}
            style={{
              display: 'flex',
              backgroundColor: '#FFF3E0',
              padding: '15px',
              borderRadius: '8px',
              alignItems: 'flex-start',
              marginTop: '20px'
            }}
          >
            <AlertCircle size={20} style={{ color: '#FF9800', marginRight: '8px', flexShrink: 0 }} />
            <span style={{
              fontSize: '14px',
              color: '#666',
              flex: 1,
              lineHeight: '20px'
            }}>
              {paymentTiming === 'before' 
                ? 'Complete your pre-trip payment to confirm arrival at destination.'
                : 'Complete payment and confirm arrival to finish your trip.'
              }
            </span>
          </motion.div>
        )}
      </motion.div>

      {/* Payment Modal */}
      {paymentModalVisible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#FFF',
            padding: '20px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '15px',
              textAlign: 'center'
            }}>
              Complete Payment
            </h3>
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#FFC000',
                textAlign: 'center',
                marginBottom: '15px'
              }}>
                {formatCurrency(delivery_cost_proposed)}
              </div>
              <p style={{
                fontSize: '14px',
                color: '#666',
                textAlign: 'center',
                margin: 0
              }}>
                Please complete your payment to finish the trip.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setPaymentModalVisible(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: '#f5f5f5',
                  color: '#333'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handlePaymentComplete({ 
                  status: 'completed', 
                  amount: parseFloat(delivery_cost_proposed || 0) 
                })}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: '#FFC000',
                  color: '#FFF'
                }}
              >
                Pay Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivateTripComplete;
